import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import LoadScreen from '../../../components/load-screen';
import SetSummaryForm from '../../../components/reporting/set-summary/SetSummaryForm';
import SetSummaryTable from '../../../components/reporting/set-summary/SetSummaryTable';

import {
  getSets,
  getLabels,
  getConsignedSets,
  exportSetSummary,
  exportAvailableSets,
} from '../../../actions/setsActions';
import { getHospitals } from '../../../actions/hospitalsActions';
import { getKits } from '../../../actions/kitsActions';
import { getItemsByIds } from '../../../actions/itemsActions';

import useLoading from '../../../hooks/useLoading';
import useAlert from '../../../hooks/useAlert';
import { ConfirmationModal } from '../../../components/shared/modal';

const SetSummaryReportingPage = () => {
  const dispatch = useDispatch();

  const { loading: fetching, startLoading, stopLoading } = useLoading(false);
  const { showAlert } = useAlert();

  const kits = useSelector((state) => state?.kits.list);
  const hospitals = useSelector((state) => state?.hospitals.list);
  const labels = useSelector((state) => state?.sets.labels);
  const cases = useSelector((state) => state?.cases.list);
  const currentTenant = useSelector((state) => state.tenant.currentTenant);

  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [data, setData] = useState([]);
  const [relatedItems, setRelatedItems] = useState([]);
  const [status, setStatus] = useState('NOT_CONSIGNED');
  const [exportModalOpen, toggleExportModal] = useState(false);

  useEffect(() => {
    onLoad().catch((err) => console.error(err));
  }, []);

  const onLoad = async () => {
    startLoading()
    try {
      await Promise.all([
        dispatch(getKits()),
        dispatch(getHospitals()),
        dispatch(getLabels()),
      ]);
    } catch (err) {
      console.error(err);
    } finally {
      stopLoading();
    }
  };

  const onSubmit = async (status) => {
    setLoading(true);
    try {
      if (status === 'CONSIGNED') {
        const sets = await dispatch(getConsignedSets(currentTenant.id, false));
        setData(sets?.filter((s) => !!s.active));
        setRelatedItems([]);
      } else if (status === 'QUARANTINED') {
        const sets = await dispatch(getSets(currentTenant.id, false));
        const quarantinedSets = sets?.filter((s) => !!s?.quarantined && !!s.active);
        const relatedItemIds = Array.from(new Set(quarantinedSets?.flatMap((set) => set.quarantineItems || []) || []));
        const items = relatedItemIds.length ? await dispatch(getItemsByIds(relatedItemIds)) : [];

        setData(quarantinedSets);
        setRelatedItems(items);
      } else {
        const sets = await dispatch(getSets(currentTenant.id, false));
        setData(sets?.filter((s) => !s?.consigned && !s?.quarantined && !!s.active));
        setRelatedItems([]);
      }
      // for (const set of sets) {
      //   let items = [];
      //   if (set.status === setPositionOptions.AVAILABLE.value) {
      //     const res = await dispatch(getSections(set.kit));
      //
      //     for (const section of res) {
      //       const res = await dispatch(getItemsByIds(section.items?.filter((i) => i.type !== billOfMaterialItemTypes.NOTES.value)?.map((i) => i.id)));
      //       items = [
      //         ...items,
      //         ...res
      //       ];
      //     }
      //   }
      //   data.push({ ...set, items });
      // }
      // setData(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const onExport = async (data) => {
    try {
      setExportLoading(true);
      await dispatch(exportSetSummary(data));
    } catch (err) {
      console.error(err);
    } finally {
      setExportLoading(false);
    }
  };

  const onExportAvailableSets = async () => {
    try {
      setExportLoading(true);
      const result = await dispatch(exportAvailableSets());
      toggleExportModal(false);
      showAlert('success', `The full inventory report has been emailed to ${result.email}`);
    } catch (err) {
      console.error(err);
      showAlert('error', err.message || 'Unable to generate the full inventory report');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className='page-container reporting-page__container'>
      {fetching && <LoadScreen />}
      <div className='page-title'>Set Summary</div>
      <div className='reporting-page__body'>
        <SetSummaryForm
          status={status}
          setStatus={setStatus}
          onSubmit={onSubmit}
          loading={loading}
          exportLoading={exportLoading}
          onExportAvailableSets={() => toggleExportModal(true)}
        />

        <SetSummaryTable
          sets={data}
          kits={kits}
          cases={cases}
          hospitals={hospitals}
          tenantColor={currentTenant.colorPrimary}
          labels={labels}
          relatedItems={relatedItems}
          exportLoading={exportLoading}
          onExportClick={onExport}
        />

        <ConfirmationModal
          open={!!exportModalOpen}
          onClose={() => toggleExportModal(false)}
          onSubmit={onExportAvailableSets}
          title='Full Inventory Report'
          text='This report includes every set at every status and all BOM item quantities. It will be generated and emailed to you as an Excel attachment.'
          submitText="Generate"
          loading={exportLoading}
        />
      </div>
    </div>
  );
};

export default SetSummaryReportingPage;
